import type { Organization } from '@/lib/services/organizations/organizations.types'
import type { User } from '@/lib/services/users/users.types'
import type { AdminRouterContext } from '@/lib/router-context'
import { RecordHistoryPage } from '@/components/audit-log/RecordHistoryPage'
import { usePathHelper } from '@/hooks/usePathHelper'
import { adminTitleHead } from '@/lib/admin-route-title'
import { auditLogQueries } from '@/lib/services/audit-log/audit-log.query'
import { organizationQueries } from '@/lib/services/organizations/organizations.query'
import { userQueries } from '@/lib/services/users/users.query'
import { userSchema } from '@/lib/services/users/users.schema'
import { loadRouteDataOrNotFound } from '@/lib/route-loader'
import { canAccessUsersInOrganization } from '@/lib/user-access'
import { createFileRoute, notFound } from '@tanstack/react-router'

export const Route = createFileRoute('/_authenticated/$orgId/users/$userId/history')({
  component: UserHistoryPage,
  params: {
    parse: (params) => userSchema.pick({ userId: true }).parse(params),
  },
  head: ({ loaderData, params }) =>
    adminTitleHead(
      'History',
      loaderData?.user?.name || `User #${params?.userId}`,
      'Users',
      'Settings'
    ),
  loader: loadUserHistory,
})

export async function loadUserHistory({
  context,
  params,
}: {
  context: AdminRouterContext & { authState?: { user?: { id: number | string } | null } }
  params: { orgId: number | string; userId: number | string }
}): Promise<{ organization: Organization; user: User }> {
  if (!context.ability.can('view', 'audit-logs')) throw notFound()

  const organization = await context.queryClient.ensureQueryData(
    organizationQueries.show(params.orgId)
  )
  const isViewingSelf = Number(params.userId) === Number(context.authState?.user?.id)

  if (!canAccessUsersInOrganization(context.ability, organization) && !isViewingSelf) {
    throw notFound()
  }

  const [user] = await loadRouteDataOrNotFound(
    () =>
      Promise.all([
        context.queryClient.ensureQueryData(
          userQueries.show({
            organizationId: params.orgId,
            userId: params.userId,
          })
        ),
        context.queryClient.ensureInfiniteQueryData(
          auditLogQueries.record({
            organizationId: params.orgId,
            subject: 'users',
            recordId: params.userId,
          })
        ),
      ]),
    notFound
  )

  return { organization, user }
}

export function UserHistoryPage() {
  const { orgId, userId } = Route.useParams()
  const { organization, user } = Route.useLoaderData()
  const path = usePathHelper()

  return (
    <RecordHistoryPage
      backLabel="user"
      backLink={path.users.show(userId).link}
      description="Review important changes to this user's profile, access, and role assignments."
      organizationId={organization.id ?? orgId}
      recordId={userId}
      recordLabel={user.name || `User #${userId}`}
      subject="users"
    />
  )
}
